import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcels } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Parcelas de um contrato financeiro.
 *
 * O endpoint devolve a coleção completa — não há paginação server-side, e por
 * isso a tela não tem controles de página.
 *
 * @param financialPersonId - ID do contrato financeiro; `null` desabilita a
 *   query, evitando uma request com `NaN` na URL.
 *
 * @example
 * ```tsx
 * const { parcels, isLoading, isError, refetch } = useIndividualPaymentParcels(101);
 * ```
 */
export function useIndividualPaymentParcels(financialPersonId: number | null) {
  const { data, isLoading, isError, refetch } =
    useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcels(
      financialPersonId ?? 0,
      undefined,
      { query: { enabled: financialPersonId !== null } },
    );

  // O Kubb tipa o corpo como array, mas o backend já foi observado envelopando
  // respostas desta API em `{ data: [...] }` — sem normalizar, o `.map` da
  // tabela quebra em runtime.
  const parcels: readonly IndividualPaymentParcelViewModel[] =
    unwrapPagedResponse<IndividualPaymentParcelViewModel>(data);

  return {
    parcels,
    isLoading: financialPersonId !== null && isLoading,
    isError,
    refetch,
  } as const;
}
